import { Box, Flex, Heading, Skeleton, Text } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { InstallationContext } from 'components/machine/dialogs/installation/context';
import { format } from 'date-fns-tz';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import {
  LOCAL_DATETIME_FORMAT_SHORT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import slugify from 'slugify';
import useMiscStore from 'stores/use-misc-store';

interface IProps {
  machineID: string;
}

export const RepeatabilityResults = (props: IProps) => {
  const { rt, installation, machinePerformance, setMachinePerformance } =
    useContext(InstallationContext);

  const [loading, setLoading] = useState(true);

  // lazily auto-generate on mount
  useEffect(() => {
    const generateReport = async () => {
      if (!rt) {
        return;
      }

      setLoading(true);

      const results =
        await AdminMachinesService.getInstance().getRepeatabilityImages({
          machineID: props.machineID,
          ball_type: installation.ball_type,
          collectionID: rt.collectionID,
          aiming: false,
          pitch_list_ids: [rt.listID],
          exclude_low_confidence: !rt.include_low_confidence,
          exclude_inactive: false,
          center_groups: true,
          min_groups: 1,
          limit: 1000,
          training: null,
          check_confidence: false,
          check_spin: false,
          check_breaks: false,
          split_by_tracking_device: false,
          build_images: true,
        });

      console.debug(
        `loaded machine performance results for ${rt.collectionID}`,
        results
      );

      setMachinePerformance(results);
      setLoading(false);
    };

    generateReport();
  }, [installation.ball_type, rt, props.machineID]);

  const content = useMemo(() => {
    if (loading) {
      return <Skeleton height="400px" />;
    }

    if (!machinePerformance || machinePerformance.images.length === 0) {
      return (
        <Flex
          pt="5"
          pb="5"
          align="center"
          direction="column"
          gap="2"
          title={machinePerformance ? 'No images' : 'No performance'}
        >
          <img
            className="select-none"
            src={useMiscStore.getState().getThemeImgPath('no-data.svg')}
            alt="no data"
            style={{
              width: '80%',
              maxWidth: '462px',
              height: 'auto',
            }}
          />

          <Heading size="3">No Data to Display</Heading>

          <Text color={RADIX.COLOR.SECONDARY}>
            Please complete data collection to explore the results here.
          </Text>
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap="2">
        <CommonCallout
          color={RADIX.COLOR.ACCENT}
          text={`Last report was generated on ${format(
            machinePerformance.datestamp ?? new Date(),
            LOCAL_DATETIME_FORMAT_SHORT,
            { timeZone: LOCAL_TIMEZONE }
          )}.`}
        />

        {machinePerformance.images.map((img, i) => (
          <Box key={`image-${i}`}>
            <img
              src={`data:image/png;base64, ${img.base64}`}
              style={{ width: '100%', height: 'auto' }}
              title="Click to save the graph as an image"
              alt={img.filename}
              className="cursor-pointer"
              onClick={async () => {
                const slug = slugify(img.filename, {
                  replacement: '_',
                  trim: true,
                });

                const url = `data:image/${img.extension};base64, ${img.base64}`;
                const res = await fetch(url);
                const blob = await res.blob();

                MiscHelper.saveAs(blob, `${props.machineID}-${slug}`);
              }}
            />
          </Box>
        ))}
      </Flex>
    );
  }, [loading, machinePerformance]);

  return (
    <ErrorBoundary identifier="MachinePerformanceTab">{content}</ErrorBoundary>
  );
};
